/**
 * <product-faq>
 *
 * Accessible product FAQ accordion.
 *
 * Behavior:
 *   - Multi-expand: each trigger toggles independently.
 *   - Deep-link: if URL hash matches a panel id, open it on load.
 *   - State reflected via aria-expanded / aria-hidden / hidden attributes.
 *
 * Markup contract (rendered by sections/product-faq.liquid):
 *   <product-faq>
 *     <button class="product-faq__trigger"
 *             aria-expanded
 *             aria-controls="PANEL_ID"> ... </button>
 *     <div class="product-faq__panel"
 *          id="PANEL_ID"
 *          aria-hidden
 *          hidden> ... </div>
 */

class ProductFaq extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.maybeOpenFromHash();
  }

  onClick = (event) => {
    const trigger = event.target.closest('.product-faq__trigger');
    if (!trigger || !this.contains(trigger)) return;

    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId && this.querySelector('#' + CSS.escape(panelId));
    if (!panel) return;

    trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    panel.setAttribute('aria-hidden', expanded ? 'true' : 'false');
    if (expanded) {
      panel.setAttribute('hidden', '');
    } else {
      panel.removeAttribute('hidden');
    }
  };

  maybeOpenFromHash() {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    let target;
    try {
      target = this.querySelector(hash);
    } catch (_) {
      return;
    }
    if (!target || !target.classList.contains('product-faq__panel')) return;

    const trigger = this.querySelector('[aria-controls="' + target.id + '"]');
    if (!trigger) return;

    trigger.click();
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

if (!customElements.get('product-faq')) {
  customElements.define('product-faq', ProductFaq);
}
